import { ThemeEvents } from '@theme/events';

const RETRY_DELAY_MS = 300;
const MAX_RETRIES = 20;

/**
 * @typedef {{ rate?: string | number }} Studio19Rate
 * @typedef {{ min_rate?: string | number, rates?: Studio19Rate[] }} Studio19VariantData
 */

/** @param {string | number | null | undefined} value */
function parseRate(value) {
  const numeric = Number.parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

/** @param {Studio19VariantData | null | undefined} variantData */
function getRateFromVariantData(variantData) {
  if (!variantData || typeof variantData !== 'object') return null;

  const minRate = parseRate(variantData.min_rate);
  if (minRate !== null) return minRate;

  if (!Array.isArray(variantData.rates)) return null;

  /** @type {number | null} */
  let minFromRates = null;
  for (const rateEntry of variantData.rates) {
    const parsedRate = parseRate(rateEntry?.rate);
    if (parsedRate === null) continue;
    if (minFromRates === null || parsedRate < minFromRates) {
      minFromRates = parsedRate;
    }
  }

  return minFromRates;
}

/** @param {number} rate */
function formatRate(rate) {
  if (!Number.isFinite(rate)) return null;

  const rounded = Math.round(rate * 100) / 100;
  if (Number.isInteger(rounded)) return `$${rounded}`;

  return `$${rounded.toFixed(2)}`;
}

function getCurrentVariantId() {
  const urlVariant = new URL(window.location.href).searchParams.get('variant');
  if (urlVariant) return urlVariant;

  const formVariantInput = document.querySelector('.product-details product-form-component input[name="id"]');
  if (formVariantInput instanceof HTMLInputElement && formVariantInput.value) {
    return formVariantInput.value;
  }

  const selectedOption = document.querySelector('variant-picker [data-variant-id][selected], variant-picker [data-variant-id]:checked');
  if (selectedOption instanceof HTMLElement && selectedOption.dataset.variantId) {
    return selectedOption.dataset.variantId;
  }

  return null;
}

function getTopStudio19TextNodes() {
  const logoNodes = Array.from(document.querySelectorAll('.product-details img[src*="Studio19_Logo"]'));

  const logoRowParagraphs = logoNodes
    .map((logo) => {
      const iconContainer = logo.closest('span, div');
      const textContainer = iconContainer?.previousElementSibling;
      if (!textContainer) return null;
      const paragraph = textContainer.querySelector('p');
      return paragraph instanceof HTMLParagraphElement ? paragraph : null;
    })
    .filter((paragraph) => paragraph !== null);

  if (logoRowParagraphs.length) return logoRowParagraphs;

  return Array.from(document.querySelectorAll('.product-details p')).filter((paragraph) => {
    const text = (paragraph.textContent || '').trim();
    return /rent\s+from/i.test(text) && /week\s+with/i.test(text);
  });
}

function updateTopStudio19Rate() {
  const variantId = getCurrentVariantId();
  if (!variantId) return false;

  /** @type {Record<string, Studio19VariantData> | undefined} */
  const allVariantData = /** @type {{ initialVariantData?: Record<string, Studio19VariantData> }} */ (window)
    .initialVariantData;
  if (!allVariantData || typeof allVariantData !== 'object') return false;

  const variantData = allVariantData[String(variantId)];
  const rate = getRateFromVariantData(variantData);
  const formattedRate = rate !== null ? formatRate(rate) : null;

  if (!formattedRate) return false;

  const textNodes = getTopStudio19TextNodes();
  if (!textNodes.length) return false;

  textNodes.forEach((paragraph) => {
    paragraph.textContent = `or Rent from ${formattedRate}/week with`;
  });

  return true;
}

function syncTopStudio19Rate(retriesLeft = MAX_RETRIES) {
  const didUpdate = updateTopStudio19Rate();

  if (!didUpdate && retriesLeft > 0) {
    window.setTimeout(() => syncTopStudio19Rate(retriesLeft - 1), RETRY_DELAY_MS);
  }
}

function isProductTemplate() {
  return document.body?.dataset.template?.includes('product') ?? false;
}

if (isProductTemplate()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncTopStudio19Rate(), { once: true });
  } else {
    syncTopStudio19Rate();
  }

  document.addEventListener(ThemeEvents.variantUpdate, () => {
    syncTopStudio19Rate();
  });
}
